import PortalIdParser from 'PortalIdParser';
import http from 'hub-http/clients/apiClient';
import allSettled from 'framework-asset-preview-lib/internal/shared/utils/allSettled';
import { getPropertyValue, setPropertyValue } from 'framework-asset-preview-lib/internal/shared/utils/crmObject';
import { getCanLoadAutomationByEmail, getNonCrmPropertyValue } from 'framework-asset-preview-lib/internal/objectTypes/0-29/utils';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import cachedHttp from 'framework-asset-preview-lib/internal/shared/api/cachedHttp';
import { DEFAULT_REVENUE_RESPONSE, INBOUND_LIST_PROPERTIES } from 'framework-asset-preview-lib/internal/objectTypes/0-29/constants';
import cloneDeep from 'hs-lodash/cloneDeep';
export function fetchMarketingEmailDetails(crmObject) {
  const emailId = getPropertyValue(crmObject, 'hs_origin_asset_id');
  return cachedHttp.get(`cosemail/v1/details/${emailId}`);
}
export function fetchEmailPerformance(emailId) {
  return cachedHttp.get(`cosemail-stats/v1/details/statistics?contentId=${emailId}`);
}
export function fetchEmailOpenDurationAggregated(nonCrmObject) {
  const hsAllCampaignIds = getNonCrmPropertyValue('allCampaignIds', nonCrmObject);
  if (!hsAllCampaignIds) {
    return Promise.resolve({
      glanced: 0,
      read: 0,
      skimmed: 0
    });
  }
  return cachedHttp.get('email/search/v1/event-digest/open-duration-aggregated', {
    query: {
      emailCampaignIds: hsAllCampaignIds
    }
  });
}
function getCrmWorkflows(flowIds) {
  return cachedHttp.post('crm-search/search', {
    data: {
      count: flowIds.length,
      filterGroups: [{
        filters: [{
          operator: 'IN',
          values: flowIds,
          property: 'hs_flow_id'
        }]
      }],
      objectTypeId: OBJECT_TYPE.WORKFLOW,
      offset: 0,
      query: '',
      requestOptions: {
        properties: ['hs_flow_id', 'hs_object_id', 'hs_enabled', 'hs_name', 'hs_uuid', 'hs_source_app']
      }
    }
  }).then(response => {
    return response.results;
  });
}
export function fetchAssociatedWorkflowsAsCrmObjects(crmObject) {
  const emailId = getPropertyValue(crmObject, 'hs_origin_asset_id');
  const uuid = `${PortalIdParser.get()}-${emailId}`;
  const hasWorkflows = getCanLoadAutomationByEmail(crmObject);
  const workflowsPromise = hasWorkflows ? cachedHttp.get(`cosemail/v1/emails/${emailId}/workflows`) : Promise.resolve([]);
  const automationPromise = cachedHttp.get('automationapps/v1/summaries', {
    query: {
      limit: 25,
      sortBy: 'updatedAt',
      sortOrder: 'descending',
      uuid
    }
  });
  return allSettled([workflowsPromise, automationPromise]).then(result => {
    const [workflowsResult, automationResult] = result;
    let allWorkflows = [];
    if (workflowsResult.status === 'fulfilled') {
      allWorkflows = [...allWorkflows, ...workflowsResult.value.map(wf => wf.id)];
    }
    if (automationResult.status === 'fulfilled') {
      allWorkflows = [...allWorkflows, ...automationResult.value.results.map(af => af.flowId)];
    }
    return getCrmWorkflows(allWorkflows);
  }).catch(() => []);
}
export function fetchAssociatedListsAsCrmObjects({
  nonCrmObject,
  isIncluded
}) {
  const includedListIds = getNonCrmPropertyValue('mailingIlsListsIncluded', nonCrmObject);
  const excludedListIds = getNonCrmPropertyValue('mailingIlsListsExcluded', nonCrmObject);
  const listIds = isIncluded ? includedListIds : excludedListIds;
  if (listIds.length === 0) {
    return Promise.resolve([]);
  }
  return cachedHttp.post('inbounddb-lists/v1/segments-ui/searchLists', {
    data: {
      fetchAllProperties: false,
      includePrivate: false,
      properties: INBOUND_LIST_PROPERTIES,
      listFilterProperties: {
        objectTypeId: OBJECT_TYPE.CONTACT,
        listIds
      }
    }
  }).then(response => {
    return response.results.map(list => ({
      objectId: list.objectId,
      objectTypeId: OBJECT_TYPE.OBJECT_LIST,
      portalId: PortalIdParser.get(),
      properties: {
        hs_list_name: {
          value: list.properties.hs_list_name && list.properties.hs_list_name.value
        },
        hs_classic_list_id: {
          value: list.properties.hs_classic_list_id && list.properties.hs_classic_list_id.value
        },
        hs_object_type_id: {
          value: list.properties.hs_object_type_id && list.properties.hs_object_type_id.value
        }
      }
    }));
  }).catch(() => []);
}
export function fetchAssociatedContactsAsCrmObjects({
  nonCrmObject,
  isIncluded
}) {
  const vidsIncluded = getNonCrmPropertyValue('vidsIncluded', nonCrmObject);
  const vidsExcluded = getNonCrmPropertyValue('vidsExcluded', nonCrmObject);
  const vids = isIncluded ? vidsIncluded : vidsExcluded;
  if (vids.length === 0) {
    return Promise.resolve([]);
  }
  return cachedHttp.post('crm-search/search', {
    data: {
      count: vids.length,
      offset: 0,
      objectTypeId: OBJECT_TYPE.CONTACT,
      requestOptions: {
        properties: ['email']
      },
      filterGroups: [{
        filters: [{
          operator: 'IN',
          property: 'hs_object_id',
          values: vids
        }]
      }]
    }
  }).then(response => {
    return response.results.map(contact => ({
      objectId: contact.objectId,
      objectTypeId: OBJECT_TYPE.CONTACT,
      portalId: PortalIdParser.get(),
      properties: {
        email: {
          value: contact.properties.email && contact.properties.email.value
        }
      }
    }));
  }).catch(() => []);
}
export function fetchEmailRevenue(nonCrmObject) {
  const hsAllCampaignIds = getNonCrmPropertyValue('allCampaignIds', nonCrmObject);
  if (!hsAllCampaignIds || hsAllCampaignIds.length === 0) {
    return Promise.resolve(DEFAULT_REVENUE_RESPONSE);
  }
  const revenueRequest = {
    metrics: [{
      property: 'hs_linear_amount',
      metricTypes: ['SUM']
    }, {
      property: 'hs_deal_id',
      metricTypes: ['DISTINCT_APPROX']
    }, {
      property: 'hs_contact_id',
      metricTypes: ['DISTINCT_APPROX']
    }],
    filterGroups: [{
      filters: [{
        values: hsAllCampaignIds,
        operator: 'IN',
        propertySuffix: 'raw',
        property: 'hs_email_campaign_id'
      }, {
        operator: 'EQ',
        value: 'EMAIL',
        property: 'hs_asset_type'
      }, {
        property: 'hs_deal_close_date',
        operator: 'HAS_PROPERTY'
      }]
    }],
    objectTypeId: 'ATTRIBUTION',
    portalId: PortalIdParser.get()
  };
  return cachedHttp.post('crm-search/report', {
    data: revenueRequest
  });
}
export async function updateMarketingEmail({
  crmObject,
  changes
}) {
  const hsContentId = getPropertyValue(crmObject, 'hs_origin_asset_id', '');

  /**
   * Create a copy of the existing crm object applying new values coming from @changes
   * This API doesnt return a CRM object as a response so we have to manually return
   * the updated CRM object
   */
  let updatedCRMObject = cloneDeep(crmObject);
  const payload = {};
  Object.keys(changes).forEach(propertyName => {
    updatedCRMObject = setPropertyValue(updatedCRMObject, propertyName, changes[propertyName]);
    if (propertyName === 'hs_name') {
      payload['name'] = changes[propertyName];
    } else {
      if (!payload.customProperties) {
        payload.customProperties = {
          propertyValues: []
        };
      }
      payload.customProperties.propertyValues.push({
        name: propertyName,
        value: changes[propertyName]
      });
    }
  });
  await http.put(`cosemail/v1/emails/${hsContentId}`, {
    data: payload
  });
  return updatedCRMObject;
}